import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Divider, IconButton, Tooltip, Chip, keyframes } from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { Terminal } from "lucide-react";
import { formatDateToLocal } from '../../../utils/dateUtils';

const blinkAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const scrollBarStyle = {
  '&::-webkit-scrollbar': {
    width: '8px',
    backgroundColor: '#020617',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'var(--fx-text-secondary)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#020617',
  }
};

const ExecutionLogsCard = ({ colors, logs, defaultExpanded, defaultShowDetails }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(defaultShowDetails || false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const logsEndRef = useRef(null);
  const containerRef = useRef(null);

  const handleDownloadLogs = () => {
    if (!logs || logs.length === 0) {
      return;
    }

    // Create log content
    const logContent = logs
        .filter(log => !log.message || typeof log.message !== 'string' || !log.message.includes("DeprecationWarning"))
        .map(log => {
            const detailTag = log.detailed ? '[DETAIL] ' : '';
            const levelTag = `[${log.level.toUpperCase()}] `;
            return `[${log.timestamp}] ${detailTag}${levelTag}${formatLogText(log.message)}`;
        }).join('\n');

    // Create blob and download
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with timestamp
    const now = new Date();
    const filename = `training-logs-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.txt`;
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Filter logs based on detail setting
  const filteredLogs = (logs || []).filter(log => {
    // Filter out noisy DeprecationWarnings
    if (log.message && typeof log.message === 'string' && log.message.includes("DeprecationWarning")) {
        return false;
    }

    if (showDetails) {
      return true; // Show all logs when details is enabled
    }
    return !log.detailed; // Show only non-detailed logs by default
  });

  const detailedCount = (logs || []).filter(log => log.detailed).length;

  const formatLogText = (text) => {
    if (!text || typeof text !== 'string') return text;
    // Replace absolute paths containing /backend/ with relative path
    // Example: config_file=/Users/user/project/backend/flwr/config.yaml -> config_file=flwr/config.yaml
    return text.replace(/=\/.*?\/backend\//g, '=');
  };

  // Check for overflow
  useEffect(() => {
    console.log('[ExecutionLogsCard] Receiving logs:', logs ? logs.length : 'null');
    const checkOverflow = () => {
      if (containerRef.current) {
        const { scrollHeight, clientHeight } = containerRef.current;
        setIsOverflowing(scrollHeight > clientHeight);
      }
    };
    
    checkOverflow();
    // Add resize listener just in case
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [logs, showDetails, isExpanded]);

  // Auto-scroll removed as we now show newest logs at the top
  // useEffect(() => {
  //   if (logsEndRef.current && !isExpanded) {
  //     logsEndRef.current.scrollIntoView({ behavior: "smooth" });
  //   }
  // }, [logs, isExpanded]);

  const renderLogMessage = (log, isLast = false) => {
    // Terminal Colors
    const baseColor = 
            log.level === 'info' ? '#22d3ee' : // Cyan
            log.level === 'success' ? '#4ade80' : // Green
            log.level === 'warning' ? '#fbbf24' : // Amber
            log.level === 'error' ? '#f87171' : // Red
            log.level === 'debug' ? 'var(--fx-text-muted)' : // Slate
            'var(--fx-border)'; // Light Slate

    const baseStyle = {
      color: baseColor,
      fontSize: log.detailed ? '0.8rem' : '0.875rem',
      opacity: log.detailed ? 0.9 : 1,
      fontFamily: 'inherit',
      lineHeight: 1.5
    };
    
    // Cursor Element
    const cursor = isLast ? (
        <Box 
            component="span" 
            sx={{ 
                display: 'inline-block', 
                width: '0.6em', 
                height: '1.2em', 
                backgroundColor: baseColor, 
                verticalAlign: 'text-bottom',
                ml: '1px',
                animation: `${blinkAnimation} 1s step-end infinite`
            }} 
        />
    ) : null;

    const FLOWER_PREFIX = '[FLOWER_STATUS] Raw task status for';
    if (log.detailed && typeof log.message === 'string' && log.message.startsWith(FLOWER_PREFIX)) {
      const firstBrace = log.message.indexOf('{');
      if (firstBrace !== -1) {
        const header = log.message.slice(0, firstBrace).trim();
        const jsonText = log.message.slice(firstBrace);
        try {
          const data = JSON.parse(jsonText);
          const taskId = data?.task_id;
          const status = data?.status;
          const progress = data?.progress || {};
          const overrides = data?.overrides || {};

          const lines = [];
          lines.push(header);
          if (status) {
            lines.push(`Status: ${status}`);
          }

          const progressParts = [];
          if (typeof progress.current_round === 'number' && typeof progress.total_rounds === 'number') {
            progressParts.push(`round ${progress.current_round}/${progress.total_rounds}`);
          }
          if (typeof progress.percentage === 'number') {
            progressParts.push(`${progress.percentage}%`);
          }
          if (progressParts.length > 0) {
            lines.push(`Progress: ${progressParts.join(' · ')}`);
          }

          if (taskId) {
            lines.push(`Task ID: ${taskId}`);
          }

          // Parse selected_datasets from the raw Python list string so we can render
          // a clean numbered list instead of the raw repr.
          let parsedDatasets = null;
          const rawSelected = overrides.selected_datasets;
          if (rawSelected && typeof rawSelected === 'string') {
            try {
              // Python list repr uses single-quotes; convert to valid JSON first.
              const jsonStr = rawSelected.replace(/'/g, '"');
              const arr = JSON.parse(jsonStr);
              if (Array.isArray(arr) && arr.length > 1) {
                parsedDatasets = arr.map(p => p.split('/').pop());
              }
            } catch (_) {/* fall through */}
          }

          const overrideKeys = Object.keys(overrides);
          if (overrideKeys.length > 0) {
            if (parsedDatasets) {
              // Show datasets as a numbered block
              lines.push(`Datasets (${parsedDatasets.length}):`);
              parsedDatasets.forEach((ds, i) => lines.push(`  [${i + 1}] ${ds}`));
              // Show remaining overrides (skip selected_datasets to avoid duplication)
              const otherPairs = overrideKeys
                .filter(k => k !== 'selected_datasets')
                .map(key => formatLogText(`${key}=${overrides[key]}`));
              if (otherPairs.length > 0) {
                lines.push(`Overrides: ${otherPairs.join(', ')}`);
              }
            } else {
              const overridePairs = overrideKeys.map((key) => formatLogText(`${key}=${overrides[key]}`));
              lines.push(`Overrides: ${overridePairs.join(', ')}`);
            }
          }
          
          return (
            <Typography 
              component="span"
              variant="body2" 
              sx={{ 
                ...baseStyle,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                display: 'inline'
              }}
            >
              {lines.join('\n')}
              {cursor}
            </Typography>
          );
        } catch (e) {
          // If parsing fails, fall back to default rendering below
        }
      }
    }

    return (
      <Typography 
        component="span"
        variant="body2" 
        sx={{
          ...baseStyle,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          display: 'inline'
        }}
      >
        {formatLogText(log.message)}
        {cursor}
      </Typography>
    );
  };

  return (
    <Card sx={{
      display: 'flex',
      flexDirection: 'column',
      height: isExpanded ? '100%' : 'fit-content',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      border: defaultExpanded ? 'none' : '1px solid var(--fx-border)',
      borderRadius: 2,
      boxShadow: 'var(--fx-shadow-sm)',
      overflow: 'hidden',
    }}>
      <CardContent sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        '&:last-child': { pb: 0 }
      }}>
        {/* Header Section */}
        <Box sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid var(--fx-border)',
          bgcolor: 'var(--fx-paper)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 26, height: 26, borderRadius: 1,
                bgcolor: '#0f172a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Terminal size={14} color="#7dd3fc" />
            </Box>
            <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.005em' }}>
              {t('execute.executionLogs')}
            </Typography>
            {detailedCount > 0 && (
              <Tooltip title={t('Toggle detailed training steps')} arrow placement="top">
                <Box 
                  onClick={() => setShowDetails(!showDetails)}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(241, 245, 249, 0.5)', // bg-slate-100 like
                    border: '1px solid var(--fx-border)',
                    cursor: 'pointer',
                    transition: 'all 0.200s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(207, 250, 254, 0.5)', // Light Cyan
                      borderColor: '#22d3ee', // Cyan-400
                      boxShadow: '0 0 8px rgba(34, 211, 238, 0.15)',
                      '& .button-text': {
                        color: 'var(--fx-info-text)', // Cyan-700
                      },
                      '& .button-icon': {
                        color: 'var(--fx-info-text)',
                      }
                    }
                  }}
                >
                  {/* Icon */}
                  <Terminal size={14} className="button-icon" style={{ color: 'var(--fx-text-secondary)', transition: 'color 0.2s' }} />
                  
                  {/* Badge */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.secondary[500], // Teal-600
                    color: 'white',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    height: '18px',
                    minWidth: '18px',
                    px: 0.5,
                    borderRadius: '999px',
                  }}>
                    {detailedCount}
                  </Box>

                  {/* Text */}
                  <Typography className="button-text" variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 500, fontSize: '0.75rem', transition: 'color 0.2s' }}>
                    {showDetails ? "Hide Detailed Logs" : "View Detailed Logs"}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
          
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            
            
            <Tooltip title={t('execute.downloadLogs')}>
              <span>
                <IconButton 
                  onClick={handleDownloadLogs}
                  disabled={(logs || []).length === 0}
                  sx={{ 
                    color: colors.grey?.[500],
                    '&:hover': {
                      color: colors.blueAccent?.[500],
                      backgroundColor: colors.blueAccent?.[50] || '#e3f2fd'
                    },
                    '&.Mui-disabled': {
                      color: colors.grey?.[300]
                    }
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            {
              !defaultExpanded && (
                <Tooltip title={!isOverflowing && !isExpanded ? t('No logs to expand') : (isExpanded ? "Collapse View" : "Expand View")}>
                  <span>
                    <IconButton 
                      onClick={() => setIsExpanded(!isExpanded)}
                  size="small"
                  disabled={!isOverflowing && !isExpanded}
                  sx={{ 
                    color: colors.grey?.[500],
                    '&.Mui-disabled': { opacity: 0.3 } 
                  }}
                >
                  {isExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                </IconButton>
              </span>
            </Tooltip>
              )
            }
          </Box>
        </Box>
        
        {/* Logs Container */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            backgroundColor: '#0b1629',
            backgroundImage: 'linear-gradient(180deg, #0b1629 0%, #0f172a 100%)',
            maxHeight: isExpanded ? '100%' : defaultExpanded ? '540px' : '400px',
            overflowY: 'auto',
            p: 1.75,
            fontFamily: 'var(--fx-font-mono)',
            fontVariantNumeric: 'tabular-nums',
            fontSize: '0.75rem',
            lineHeight: 1.65,
            ...scrollBarStyle
          }}
        >
          {filteredLogs.length === 0 ? (
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.grey?.[600] || '#999999',
                fontStyle: 'italic',
                textAlign: 'center',
              }}
            >
              {(logs || []).length === 0 ? t('execute.noLogsYet') : t('execute.noLogsToShow')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {[...filteredLogs].reverse().map((log, index) => {
                // Debug log for timestamp issues
                if (index < 3) console.log('[ExecutionLogsCard] Processing log:', log);
                
                // Since we reversed the logs, the "latest" log is now at index 0
                const isLatest = index === 0;
                // Debug: verify only latest gets cursor
                if (index < 3) console.log('[ExecutionLogsCard] isLatest for index', index, ':', isLatest);
                return (
                <Box 
                  key={log.id || index} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    mb: 0,
                    p: '2px 4px',
                    borderBottom: 'none',
                    lineHeight: 1.4,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  <Typography 
                    component="span"
                    sx={{ 
                      color: colors.grey?.[600] || 'var(--fx-text-secondary)',
                      mr: 1.5, 
                      whiteSpace: 'nowrap', 
                      fontFamily: 'inherit',
                      fontSize: '0.75rem',
                      userSelect: 'none',
                      mt: '2px'
                    }}
                  >
                    [{formatDateToLocal(log.timestamp, 'HH:mm:ss')}]
                  </Typography>
                  
                  <Box sx={{ flex: 1, display: 'block', wordBreak: 'break-word' }}>
                    {log.detailed && (
                      <Chip 
                        label="DBG"
                        size="small"
                        sx={{ 
                          height: '16px', 
                          fontSize: '0.65rem',
                          backgroundColor: 'rgba(56, 189, 248, 0.2)',
                          color: '#38bdf8',
                          fontFamily: 'inherit',
                          mr: 1,
                          border: '1px solid rgba(56, 189, 248, 0.3)',
                          verticalAlign: 'middle'
                        }}
                      />
                    )}
                    {!log.detailed && typeof log.message === 'string' && log.message.startsWith('Federated training across') && (
                      <Chip
                        label="MULTI-DS"
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '0.65rem',
                          backgroundColor: 'rgba(20, 184, 166, 0.2)',
                          color: '#2dd4bf',
                          fontFamily: 'inherit',
                          mr: 1,
                          border: '1px solid rgba(20, 184, 166, 0.35)',
                          verticalAlign: 'middle'
                        }}
                      />
                    )}
                    {renderLogMessage(log, isLatest)}
                  </Box>
                </Box>
              )})}
              <div ref={logsEndRef} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ExecutionLogsCard;
